'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as Checkbox from '@radix-ui/react-checkbox';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { CaretDown, CaretUp, Check } from 'phosphor-icons';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { FormModal, FormModalClose } from '../FormModal';
import type {
  ProductListSortingOption,
  ProductListSortingPropsMicrocopy,
} from './ProductListSorting.types';

// Accordion Components
// TODO: Move to atoms/molecules in future refactor
type AccordionItemHeaderProps = {
  title: string;
};

const AccordionItemHeader = ({ title }: AccordionItemHeaderProps) => (
  <AccordionPrimitive.Header>
    <AccordionPrimitive.Trigger className="group flex w-full items-center justify-between text-xs font-semibold uppercase leading-[40px] tracking-wider text-grey-500 outline-none focus-visible:ring-2 focus-visible:ring-blue">
      <span>{title}</span>
      <div className="p-3">
        <CaretDown
          size={16}
          weight="regular"
          className="text-grey-200 transition-transform group-data-[state=open]:hidden"
        />
        <CaretUp
          size={16}
          weight="regular"
          className="hidden text-grey-200 transition-transform group-data-[state=open]:block"
        />
      </div>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);

type AccordionItemContentProps = {
  children: React.ReactNode;
};

const AccordionItemContent = ({ children }: AccordionItemContentProps) => (
  <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
    <div className="pb-4">{children}</div>
  </AccordionPrimitive.Content>
);

type CheckboxItemProps = {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

// TODO: Consider create a checkbox component as part of the Design System. We currently have a Checkbox component in the Form folder, but it's not being used in this component.
const CheckboxItem = ({
  label,
  checked,
  onCheckedChange,
}: CheckboxItemProps) => (
  <Checkbox.Root
    checked={checked}
    onCheckedChange={(value) => onCheckedChange(value === true)}
    className="flex w-full cursor-pointer items-center gap-3 text-grey-100 outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-black"
  >
    <span
      className={c(
        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded transition-all',
        checked ? 'border-2 border-blue bg-blue' : 'border border-grey-500'
      )}
    >
      {checked && (
        <Check
          size={14}
          className="text-white"
          weight="bold"
          aria-hidden="true"
        />
      )}
    </span>
    <span className={c('text-base leading-10', checked && 'font-medium')}>
      {label}
    </span>
  </Checkbox.Root>
);

type SortOptionItemProps = {
  label: string;
  value: string;
  isSelected: boolean;
};

const SortOptionItem = ({ label, value, isSelected }: SortOptionItemProps) => (
  <RadioGroup.Item
    value={value}
    className="flex w-full cursor-pointer items-center justify-between py-3 text-grey-100 outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-black"
  >
    <span className={c('text-base', isSelected && 'font-semibold')}>
      {label}
    </span>
    {isSelected && (
      <Check
        size={16}
        className="flex-shrink-0 text-blue-dark"
        weight="bold"
        aria-hidden="true"
      />
    )}
  </RadioGroup.Item>
);

export type MobileFilterModalPropsMicrocopy = Pick<
  ProductListSortingPropsMicrocopy,
  | 'mobileButtonLabel'
  | 'mobileCloseAriaLabel'
  | 'mobileFilterTitle'
  | 'mobileSortTitle'
  | 'mobileViewResultsLabel'
  | 'mobileClearLabel'
>;

export type MobileFilterModalProps = {
  filterOptions: ProductListSortingOption[];
  sortOptions: ProductListSortingOption[];
  selectedFilters: string[];
  selectedSort: string | null;
  onFiltersChange: (values: string[]) => void;
  onSortChange: (value: string) => void;
  onClear: () => void;
  resultsCount: number;
  microcopy: MobileFilterModalPropsMicrocopy;
  className?: string;
};

export const MobileFilterModal = ({
  filterOptions,
  sortOptions,
  selectedFilters,
  selectedSort,
  onFiltersChange,
  onSortChange,
  onClear,
  resultsCount,
  microcopy,
}: MobileFilterModalProps) => {
  const handleFilterToggle = (value: string, checked: boolean) => {
    if (checked) {
      onFiltersChange([...selectedFilters, value]);
    } else {
      onFiltersChange(selectedFilters.filter((v) => v !== value));
    }
  };

  const filterCount = selectedFilters.length;
  const totalSelections = filterCount + (selectedSort ? 1 : 0);

  const filterTitle =
    filterCount > 0
      ? `${microcopy.mobileFilterTitle} (${filterCount})`
      : microcopy.mobileFilterTitle;

  const viewResultsLabel = `${microcopy.mobileViewResultsLabel} (${resultsCount})`;
  const clearLabel =
    totalSelections > 0
      ? `${microcopy.mobileClearLabel} (${totalSelections})`
      : microcopy.mobileClearLabel;

  const trigger = (
    <ButtonV2
      type="button"
      size="small"
      shape="rounded"
      variant="secondary-black-monochrome"
      className="w-full py-2 md:w-auto"
      label={microcopy.mobileButtonLabel}
      iconRight="SortAscending"
      iconWeight="bold"
      iconSize={20}
    />
  );

  const modalContent = (
    <div className="px-4">
      <div className="mx-auto w-full md:max-w-[460px]">
        <AccordionPrimitive.Root
          type="multiple"
          defaultValue={['filter']}
          className="space-y-8 pt-1 md:pt-2"
        >
          <AccordionPrimitive.Item value="filter">
            <AccordionItemHeader title={filterTitle} />
            <AccordionItemContent>
              {filterOptions.map((option) => (
                <CheckboxItem
                  key={option.value}
                  label={option.label}
                  checked={selectedFilters.includes(option.value)}
                  onCheckedChange={(checked) =>
                    handleFilterToggle(option.value, checked)
                  }
                />
              ))}
            </AccordionItemContent>
          </AccordionPrimitive.Item>

          <AccordionPrimitive.Item value="sort">
            <AccordionItemHeader title={microcopy.mobileSortTitle} />
            <AccordionItemContent>
              <RadioGroup.Root
                value={selectedSort || ''}
                onValueChange={onSortChange}
              >
                {sortOptions.map((option) => (
                  <SortOptionItem
                    key={option.value}
                    label={option.label}
                    value={option.value}
                    isSelected={selectedSort === option.value}
                  />
                ))}
              </RadioGroup.Root>
            </AccordionItemContent>
          </AccordionPrimitive.Item>
        </AccordionPrimitive.Root>
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex flex-col space-y-3 bg-black p-4 md:flex-row-reverse md:justify-end md:space-x-3 md:space-y-0">
      <FormModalClose asChild>
        <ButtonV2
          label={viewResultsLabel}
          variant="secondary"
          size="small"
          shape="rounded"
          className="w-full"
        />
      </FormModalClose>
      <ButtonV2
        label={clearLabel}
        variant="dark-tertiary"
        size="small"
        shape="rounded"
        onClick={onClear}
        className="w-full"
      />
    </div>
  );

  return (
    <FormModal
      variant="fullscreen"
      headerVariant="minimal"
      dark
      close={microcopy.mobileCloseAriaLabel}
      trigger={trigger}
      content={modalContent}
      customFooter={modalFooter}
    />
  );
};

MobileFilterModal.displayName = 'MobileFilterModal';
